import {
  generatedIssuerOrValuesFallback,
  savedIssuerOrGeneratedFallback,
} from 'features/evaluations/metadata/utils/evalEventIssuer'
import {
  generatedPageClassificationOrValuesFallback,
  savedPageClassificationOrGeneratedFallback,
} from 'features/evaluations/metadata/utils/evalEventPageClassification'
import type {
  DocumentPageMetadataUserEditEntry,
  DocumentPageMetadataUserEditSource,
  DocumentPageMetadataUserEditStatus,
  DocumentPageMetadataUserEditValues,
} from 'features/knowledgeDocumentViewer/types/documentMetadataUserEditSchemas'
import { pageMetadataUserEditCreatedByEmail } from 'features/knowledgeDocumentViewer/utils/pageMetadataEvalHistory'
import { unscopeSearchDocumentIdForHydration } from 'features/knowledgeDocumentViewer/utils/pipelineConfigScopedIds'
import { pageMetadataUserEditPageNum } from 'features/knowledgeDocumentViewer/utils/selectLatestConfirmedPageMetadataUserEdit'

export type LatestEvalLabel = {
  siteId: string
  documentId: string
  pageNum: number
  status: DocumentPageMetadataUserEditStatus
  time: number
  user: string
  source: DocumentPageMetadataUserEditSource | undefined
  pageClassification: string | undefined
  generatedPageClassification: string | undefined
  issuer: string | undefined
  generatedIssuer: string | undefined
  values: DocumentPageMetadataUserEditValues | undefined
  generatedValues: DocumentPageMetadataUserEditValues | undefined
  blindQa: boolean
}

export type EvalLabelsByUser = Map<string, Map<string, LatestEvalLabel>>

function evalQueueStatus(
  edit: DocumentPageMetadataUserEditEntry
): DocumentPageMetadataUserEditStatus | undefined {
  if (edit.entry.data.surface !== 'eval') return undefined
  const status = edit.entry.data.status
  if (status === 'confirmed' || status === 'deferred') return status
  return undefined
}

/** Canonical event time for eval labels: created_at, else updated_at. */
export function evalEventTime(edit: DocumentPageMetadataUserEditEntry): number {
  const raw = edit.entry.created_at ?? edit.entry.updated_at
  if (!raw) return 0
  const ms = Date.parse(raw)
  return Number.isFinite(ms) ? ms : 0
}

function userEditSiteId(
  edit: DocumentPageMetadataUserEditEntry
): string | undefined {
  const site = edit.entry.tags?.site
  if (site === undefined || site === null) return undefined
  const trimmed = site.trim()
  return trimmed === '' ? undefined : trimmed
}

/** Shared join key for eval labels and generated pages: siteId:documentId:pageNum. */
export function evalPageKey(
  siteId: string,
  documentId: string,
  pageNum: number
): string {
  return `${siteId}:${documentId}:${pageNum}`
}

function pageEvalIdentity(
  edit: DocumentPageMetadataUserEditEntry,
  siteId: string,
  pipelineConfigId: string | undefined
): { documentId: string; pageNum: number; pageKey: string } | undefined {
  const pageNum = pageMetadataUserEditPageNum(edit)
  if (pageNum === undefined) return undefined
  const documentId = unscopeSearchDocumentIdForHydration(
    edit.entry.data.document_id,
    edit.entry.data.pipeline_config_id ?? pipelineConfigId
  )
  if (documentId.trim() === '') return undefined
  return {
    documentId,
    pageNum,
    pageKey: evalPageKey(siteId, documentId, pageNum),
  }
}

export function evalEventIdentity(
  edit: DocumentPageMetadataUserEditEntry,
  pipelineConfigId: string | undefined
) {
  const siteId = userEditSiteId(edit)
  if (siteId === undefined || edit.entry.id.trim() === '') return undefined
  const status = evalQueueStatus(edit)
  const user = pageMetadataUserEditCreatedByEmail(edit)
  const page = pageEvalIdentity(edit, siteId, pipelineConfigId)
  if (status === undefined || user === undefined || page === undefined)
    return undefined
  return { siteId, status, user, ...page }
}

export function latestEvalByUserAndPage(
  edits: readonly DocumentPageMetadataUserEditEntry[],
  pipelineConfigId: string | undefined
): EvalLabelsByUser {
  const byUser: EvalLabelsByUser = new Map()
  for (const edit of edits) {
    const identity = evalEventIdentity(edit, pipelineConfigId)
    if (identity === undefined) continue
    const { siteId, documentId, pageNum, status, user, pageKey } = identity
    const time = evalEventTime(edit)
    const pages = byUser.get(user) ?? new Map<string, LatestEvalLabel>()
    if (!byUser.has(user)) byUser.set(user, pages)
    const current = pages.get(pageKey)
    if (current !== undefined && time <= current.time) continue
    pages.set(pageKey, {
      siteId,
      documentId,
      pageNum,
      status,
      time,
      user,
      source: edit.entry.data.source ?? undefined,
      pageClassification: savedPageClassificationOrGeneratedFallback(edit),
      generatedPageClassification:
        generatedPageClassificationOrValuesFallback(edit),
      issuer: savedIssuerOrGeneratedFallback(edit),
      generatedIssuer: generatedIssuerOrValuesFallback(edit),
      values: edit.entry.data.values ?? undefined,
      generatedValues: edit.entry.data.generated_values ?? undefined,
      blindQa: edit.entry.data.blind_qa === true,
    })
  }
  return byUser
}

export function evalLabelsBySite(
  byUser: EvalLabelsByUser
): Map<string, EvalLabelsByUser> {
  const bySite = new Map<string, EvalLabelsByUser>()
  for (const [user, pages] of byUser) {
    for (const [pageKey, label] of pages) {
      const users =
        bySite.get(label.siteId) ??
        new Map<string, Map<string, LatestEvalLabel>>()
      const sitePages = users.get(user) ?? new Map<string, LatestEvalLabel>()
      sitePages.set(pageKey, label)
      users.set(user, sitePages)
      bySite.set(label.siteId, users)
    }
  }
  return bySite
}

export function evalLabelsForSites(
  byUser: EvalLabelsByUser,
  siteIds: ReadonlySet<string>
): EvalLabelsByUser {
  if (siteIds.size === 0) return new Map()
  const next: EvalLabelsByUser = new Map()
  for (const [user, pages] of byUser) {
    const filtered = new Map<string, LatestEvalLabel>()
    for (const [pageKey, label] of pages) {
      if (siteIds.has(label.siteId)) filtered.set(pageKey, label)
    }
    if (filtered.size > 0) next.set(user, filtered)
  }
  return next
}
